import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import PrivacyPage from "../pages/privacy/page";
import PrivacyShopifyPage from "../pages/privacy-shopify/page";
import TermsPage from "../pages/terms/page";
import DataDeletionPage from "../pages/data-deletion/page";
import DemoPage from "../pages/demo/page";
import TryPage from "../pages/try/page";
import AppPage from "../pages/app/page";
import AppV2Page from "../pages/appv2/page";
import ProductShotV3Page from "../pages/appv3/page";
import CarouselV3Page from "../pages/appv3/carousel/page";
import CreatePage from "../pages/create/page";
import RecreatePage from "../pages/recreate/page";
import BrandPage from "../pages/brand/page";
import BrandsPage from "../pages/brands/page";
import NewBrandWizardPage from "../pages/brands/new";
import ProductsPage from "../pages/products/page";
import TemplatesPage from "../pages/templates/page";
import TemplateLabPage from "../pages/template-lab/page";
import CreateTemplatePage from "../pages/create-template/page";
import RenderAnimationPreviewPage from "../pages/dev-render-animation-preview/page";
import AnimationStudio from "../pages/animation/page";
import AdStrategistPage from "../pages/ad-strategist/page";
import AdStrategistV1Page from "../pages/ad-strategist-v1/page";
import PremiumRoute from "../components/feature/PremiumRoute";
import CompetitorsPage from "../pages/competitors/page";
import UgcFinderPage from "../pages/ugc-finder/page";
import UgcPage from "../pages/ugc/page";
import UgcBuilderPage from "../pages/ugc-builder/page";
import BrandImageryPage from "../pages/brand-imagery/page";
import CompetitorDetailPage from "../pages/competitors/detail";
import EmailsPage from "../pages/emails/page";
import NewEmailWizardPage from "../pages/emails/new";
import GalleryPage from "../pages/gallery/page";
import CreativeDetailPage from "../pages/creatives/detail";
import EditorV4Page from "../pages/editor-v4/page";
import EditorV5Page from "../pages/editor-v5/page";
import AnalyticsPage from "../pages/analytics/page";
import type { ReactNode } from "react";
import { useSocialPublishingEnabled, useSchedulingEnabled } from "../hooks/useFeatureFlags";
import ConnectFacebookPage from "../pages/connect-facebook/page";
import SchedulePage from "../pages/schedule/page";
import AutoSchedulePage from "../pages/schedule/auto/page";
import SignInPage from "../pages/auth/SignInPage";
import SignUpPage from "../pages/auth/SignUpPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import VerifyEmailPage from "../pages/auth/VerifyEmailPage";
import ProfilePage from "../pages/auth/ProfilePage";
import CheckoutPage from "../pages/checkout/page";
import CheckoutSuccessPage from "../pages/checkout/success";
import CheckoutCancelPage from "../pages/checkout/cancel";
import PresetEditorPage from "../pages/preset-editor/page";
import SocialTemplateBuilderPage from "../pages/social-template-builder/page";
import MoodboardBuilderPage from "../pages/moodboard-builder/page";
import MoodboardPreviewsPage from "../pages/moodboard-previews/page";
import PickerTestPage from "../pages/picker-test/page";
import ArtDirectorCastPage from "../pages/art-director-cast/page";
import VariableMixerPage from "../pages/variable-mixer/page";
import TemplateDebugPage from "../pages/template-debug/page";
import ImportTemplatePage from "../pages/import-template/page";
import BakeHarnessPage from "../pages/bake-harness/page";
import MoodboardSmokeTestPage from "../pages/moodboard-smoke-test/page";
import ResponsiveLabPage from "../pages/responsive-lab/page";
import AuthGate from "../components/feature/AuthGate";

// /animation → /appv3/animation redirect (Jun 15). Preserves the
// ?template=<id> query string so existing /templates deep-links keep
// working with the new route.
function AnimationLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={`/appv3/animation${location.search}`} replace />;
}

// Account-aware route gates (Aug 20). The FB/IG analytics + Ad Calendar/Pilot
// routes are hidden for real users while Meta App Review is pending, but
// reachable for the reviewer/team allowlist. These render at route time so the
// per-account hook (which reads the signed-in user) can decide. Providers sit
// above the router in App.tsx, so useAuth works here — same as AuthGate below.
function SocialGate({ on, off }: { on: ReactNode; off: ReactNode }) {
  return <>{useSocialPublishingEnabled() ? on : off}</>;
}
function SchedulingGate({ on, off }: { on: ReactNode; off: ReactNode }) {
  return <>{useSchedulingEnabled() ? on : off}</>;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  // /create — dashboard / landing page. First thing users land on after
  // sign-in (post May 26). Shows category tiles (clicks → /appv2?type=...)
  // and the Recreate template grid (Phase 2). The new default entry point.
  {
    path: "/create",
    element: (
      <AuthGate>
        <CreatePage />
      </AuthGate>
    ),
  },
  // /app — legacy wizard. Kept accessible during the /appv2 cutover; will
  // be retired once /appv2 + /create are proven. NEW marketing CTAs +
  // sign-in default now point at /create instead.
  {
    path: "/app",
    element: (
      <AuthGate>
        <AppPage />
      </AuthGate>
    ),
  },
  // /appv2 — the active wizard. Reads ?type= from URL (postType arrives
  // from the /create dashboard). No StepCategory section anymore — the
  // dashboard owns category selection.
  {
    path: "/appv2",
    element: (
      <AuthGate>
        <AppV2Page />
      </AuthGate>
    ),
  },
  // /appv3/product-swap → REDIRECT to product-shot (Jun 12 v6 — In Hand was
  // folded into Product Shot: its templates + poses now live there). Must
  // come BEFORE the :category route so the specific path wins.
  {
    path: "/appv3/product-swap",
    element: <Navigate to="/appv3/product-shot" replace />,
  },
  // /appv3/animation — V3 Animation Studio (Jun 15). Two-step Studio
  // layout that's structurally its own component (no FashionStudioLayout
  // — animation uses client-side WebCodecs, not backend ai-generate).
  // Registered as a specific path BEFORE /appv3/:category so the catch-
  // all doesn't route 'animation' through ProductShotV3Page. The old
  // /animation URL still works via the redirect below for back-compat
  // with /templates deep-links (?template=<id>).
  {
    path: "/appv3/animation",
    element: (
      <AuthGate>
        <AnimationStudio />
      </AuthGate>
    ),
  },
  // /appv3/carousel — V3 Carousel.
  // Jun 14 update: now routed through the SAME /appv3/:category page
  // (ProductShotV3Page) which handles the carousel category by adding
  // a type-pre-step on top of the existing Product Shot Studio UI.
  // The standalone CarouselV3Page (the v1 fallback) is kept in the
  // import below for emergency revert during the testing window — to
  // fall back, swap the element here to <CarouselV3Page /> and stash
  // this comment block.
  // (Path declaration removed — falls through to /appv3/:category.)
  // /appv3/:category — PROTOTYPE parameterized layout (dev). Single-
  // screen workflow: design fork + creative direction OR template picker
  // on the left, product picker + preview + Generate on the right.
  // Categories:
  //   /appv3/product-shot  → product hero OR held (In Hand merged in)
  //   /appv3/fashion       → Worn Item: model wears the garment
  // Unknown values fall back to product-shot. Not a replacement for
  // /appv2; this is a layout exploration.
  {
    path: "/appv3/:category",
    element: (
      <AuthGate>
        <ProductShotV3Page />
      </AuthGate>
    ),
  },
  // /appv2/recreate/:id — Recreate runtime page. User picks a template
  // on /create, lands here, uploads their product, downloads the result.
  // Bypasses the wizard entirely. See mocks/recreate-templates.ts for the
  // template catalog.
  {
    path: "/appv2/recreate/:id",
    element: (
      <AuthGate>
        <RecreatePage />
      </AuthGate>
    ),
  },
  // /brand — Brand identity for the user's ACTIVE brand (name, brief,
  // logo, colors, source URL). Multi-brand support moved CRUD to /brands;
  // this page edits whichever brand is active.
  {
    path: "/brand",
    element: (
      <AuthGate>
        <BrandPage />
      </AuthGate>
    ),
  },
  // /brands — Manage brands. List + activate + soft-delete. Add-brand
  // launches the /brands/new wizard which collects website + products +
  // kicks off 20-creative baking. Switching brand here hard-reloads the app.
  {
    path: "/brands",
    element: (
      <AuthGate>
        <BrandsPage />
      </AuthGate>
    ),
  },
  // /brands/new — Add-Brand wizard. Multi-step: website scrape → review →
  // products → confirm → background generation of 20 AI Magic creatives.
  // Outside the brand-context shell so internal state survives the
  // brand-create/activate mid-flow.
  {
    path: "/brands/new",
    element: (
      <AuthGate>
        <NewBrandWizardPage />
      </AuthGate>
    ),
  },
  // /products — My Products library. Stores up to 10 products per user
  // so they don't have to re-paste the URL on every generation. Same
  // scrape pipeline as the wizard's StepUpload URL path.
  {
    path: "/products",
    element: (
      <AuthGate>
        <ProductsPage />
      </AuthGate>
    ),
  },
  // /ugc-finder — UGC Finder. Discover TikTok/Instagram creators to partner
  // with (outreach/UGC) via ScrapeCreators, matched to the brand, + a saved
  // outreach library (prospect → contacted → partnered).
  {
    path: "/ugc-finder",
    element: (
      <AuthGate>
        <UgcFinderPage />
      </AuthGate>
    ),
  },
  // /ugc — "Fake UGC". Pick a product → auto-render it in a real creator/UGC
  // aesthetic (backend UGC recipes) → saved to My Creatives. Backend: routes/ugc.py.
  {
    path: "/ugc",
    element: (
      <AuthGate>
        <UgcPage />
      </AuthGate>
    ),
  },
  // /brand-imagery — "Brand Imagery". Pick one of the brand's own scraped photos
  // → generate 4 on-brand variations (with/without product) → My Creatives.
  // Backend: routes/brand_imagery.py.
  {
    path: "/brand-imagery",
    element: (
      <AuthGate>
        <BrandImageryPage />
      </AuthGate>
    ),
  },
  // /ugc-builder — DEV-ONLY curator for Fake UGC styles (page self-gates on
  // import.meta.env.DEV). Drop references → extract recipe → save to UGC store.
  {
    path: "/ugc-builder",
    element: (
      <AuthGate>
        <UgcBuilderPage />
      </AuthGate>
    ),
  },
  // /templates — full-page browse of all Product Shot scene templates.
  // Pin favourites here; pinned templates surface first in the wizard's
  // Background step. Storage is localStorage (lib/pinnedTemplates).
  {
    path: "/templates",
    element: (
      <AuthGate>
        <TemplatesPage />
      </AuthGate>
    ),
  },
  // /template-lab — DEV-ONLY AI template authoring harness. Paste a
  // reference → vision LLM writes a sanitized template prompt → render
  // with a mock product (medium) → Save publishes into the library via
  // Vite dev middleware. Page self-gates on import.meta.env.DEV.
  {
    path: "/template-lab",
    element: (
      <AuthGate>
        <TemplateLabPage />
      </AuthGate>
    ),
  },
  // /create-template — DEV-ONLY: convert a legacy scene background into an AI
  // template. Pick a background + mock product → place + vision-write prompt →
  // Save (full-res PNG runtime reference). Page self-gates on import.meta.env.DEV.
  {
    path: "/create-template",
    element: (
      <AuthGate>
        <CreateTemplatePage />
      </AuthGate>
    ),
  },
  // /dev/render-animation-preview — DEV-ONLY one-shot harness for baking an
  // animation template's preview MP4 from the AI sample library. Output is
  // dropped into public/animation-templates/previews/ by hand. Self-gated.
  {
    path: "/dev/render-animation-preview",
    element: (
      <AuthGate>
        <RenderAnimationPreviewPage />
      </AuthGate>
    ),
  },
  // /animation → /appv3/animation (Jun 15 — the standalone /animation
  // surface was folded into /appv3 to inherit the Studio chrome shared
  // with Product Shot / Carousel / Fashion). Old bookmarks + the
  // ?template=<id> deep-link from /templates keep working via the
  // search-preserving redirect.
  {
    path: "/animation",
    element: <AnimationLegacyRedirect />,
  },
  // Premium competitive ad-intelligence tool. Pulls competitor ads from
  // public ad libraries → brief + 5 concepts. Backend gates on plan_tier.
  {
    path: "/ad-strategist",
    element: (
      <AuthGate>
        <PremiumRoute
          feature="Ad Strategist"
          icon="ri-radar-line"
          tagline="See exactly what your competitors are running — then beat it with ads built for your brand."
          points={[
            { icon: 'ri-search-eye-line', title: "Pull competitors' live ads", body: 'Fetch their active Facebook & Instagram ads in seconds — no manual digging.' },
            { icon: 'ri-lightbulb-flash-line', title: 'Get a competitive brief', body: "See what's working, where the gaps are, and how to stand out." },
            { icon: 'ri-magic-line', title: 'Turn gaps into creatives', body: 'Generate ready-to-run ads for your brand from the winning angles.' },
            { icon: 'ri-refresh-line', title: 'Stay ahead', body: 'Refresh anytime so you always know what the market is doing.' },
          ]}
        >
          <AdStrategistPage />
        </PremiumRoute>
      </AuthGate>
    ),
  },
  // /ad-strategist-v1 — Ad Strategist V1 (Jul 23 2026). Reuses the proven
  // "Generate 20 more" top-up backend VERBATIM (Cloud Tasks render, no
  // hang) behind the AS grid. New frontend surface only — original
  // /ad-strategist + "Generate 20 more" + My Creatives all untouched.
  {
    path: "/ad-strategist-v1",
    element: (
      <AuthGate>
        <PremiumRoute
          feature="Ad Strategist"
          icon="ri-radar-line"
          tagline="See exactly what your competitors are running — then beat it with ads built for your brand."
          points={[
            { icon: 'ri-search-eye-line', title: "Pull competitors' live ads", body: 'Fetch their active Facebook & Instagram ads in seconds — no manual digging.' },
            { icon: 'ri-lightbulb-flash-line', title: 'Get a competitive brief', body: "See what's working, where the gaps are, and how to stand out." },
            { icon: 'ri-magic-line', title: 'Turn gaps into creatives', body: 'Generate ready-to-run ads for your brand from the winning angles.' },
            { icon: 'ri-refresh-line', title: 'Stay ahead', body: 'Refresh anytime so you always know what the market is doing.' },
          ]}
        >
          <AdStrategistV1Page />
        </PremiumRoute>
      </AuthGate>
    ),
  },
  // /competitors — saved competitor library. Canonical surface for
  // managing tracked brands; Ad Strategist reads from here instead of
  // doing fresh Apify scrapes on every run.
  {
    path: "/competitors",
    element: (
      <AuthGate>
        <CompetitorsPage />
      </AuthGate>
    ),
  },
  // /competitors/:id — single-competitor detail page. Replaces the prior
  // View Ads modal with a shareable, back-button-aware surface for
  // browsing the cached ads + per-competitor controls.
  {
    path: "/competitors/:id",
    element: (
      <AuthGate>
        <CompetitorDetailPage />
      </AuthGate>
    ),
  },
  // /emails — AI-generated email templates (M1 — generate + export only).
  // List of saved emails for the active brand. Click → /emails/new editor.
  {
    path: "/emails",
    element: (
      <AuthGate>
        <EmailsPage />
      </AuthGate>
    ),
  },
  // /emails/new — 3-step wizard: campaign type → template → preview/edit.
  // Optional ?id=<email-id> param resumes editing an existing email.
  {
    path: "/emails/new",
    element: (
      <AuthGate>
        <NewEmailWizardPage />
      </AuthGate>
    ),
  },
  // Dev-only preset authoring tool. Reads from textPresets.ts + templates.json,
  // writes to templates.json via the /api/templates-save Vite middleware.
  {
    path: "/preset-editor",
    element: <PresetEditorPage />,
  },
  // Dev-only visual builder for Social Post JSON templates. Reads from
  // social-designs/json-templates/*.json via the registry, writes to
  // the same files via the /api/social-templates-save Vite middleware.
  {
    path: "/text-template-builder",
    element: <SocialTemplateBuilderPage />,
  },
  // /moodboard-builder — DEV-ONLY style-reference curation harness
  // (Jun 15). Paste/drop/upload an inspiration image; gpt-4.1-mini
  // vision classifies it into one of the 5 _CD_VIBE buckets; save
  // uploads to the GCS moodboards bucket for later use as style refs
  // in the brand-setup render pipeline. Page self-gates on
  // import.meta.env.DEV.
  {
    path: "/moodboard-builder",
    element: <MoodboardBuilderPage />,
  },
  // /moodboard-previews — DEV-ONLY preview baker (Jun 18). Curator
  // picks N approved recipes + uploads ONE product → backend renders
  // each verbatim → uploads thumbnail to <vibe>/previews/<id>.png in
  // the moodboard-previews bucket → sidecar gains preview_path +
  // 3 timestamp fields. Production picker (Recipes-as-Templates phase 1)
  // will read preview_url for tile thumbnails — scraped moodboard
  // images never surface in production-facing UI.
  {
    path: "/moodboard-previews",
    element: <MoodboardPreviewsPage />,
  },
  // /picker-test — DEV-ONLY picker inspection (Jun 27). "Generate 20"
  // runs the brand-setup picker without rendering → shows the 20 picked
  // recipes so the curator can verify scene-recipe uniqueness + the
  // background+composition fusion pairing (+ camera/lighting).
  {
    path: "/picker-test",
    element: <PickerTestPage />,
  },
  // /art-director-cast — DEV-ONLY (Jul 2026). The #3 architecture: AI writes a
  // product-appropriate art direction (scene/mood/lighting/palette + AVOID),
  // then casts recipes from the full pool by craft-fit, ignoring product_type.
  // Read-only, no render — eyeball whether casting picks the right craft.
  {
    path: "/art-director-cast",
    element: <ArtDirectorCastPage />,
  },
  // /variable-mixer — DEV-ONLY (Jul 5). Mix one stable value per family from the
  // decomposed recipes (Step 1) and render an uploaded product. Prompt is built
  // only from recipe-extracted text — manual coherent-fusion tester.
  {
    path: "/variable-mixer",
    element: <VariableMixerPage />,
  },
  // /template-debug — DEV-ONLY (Jun 30). Groups a brand's text-overlay
  // creatives by text template so the curator can eyeball where a template's
  // color/placement fails/succeeds across scenes. Per-tile flip between the
  // baked composite + the text-free base scene. Reads the prod DB locally.
  {
    path: "/template-debug",
    element: <TemplateDebugPage />,
  },
  // /import-template — DEV-ONLY paste-import (Jun 27). Figma "Send to dev"
  // is blocked by the plugin sandbox (mixed content reaching localhost);
  // paste the plugin's Copy JSON here instead — same-origin, always works.
  {
    path: "/import-template",
    element: <ImportTemplatePage />,
  },
  // /bake-harness — DEV-ONLY headless render target for the Playwright text-
  // overlay bake (Jun 28). Renders JsonSocialPost from window.__BAKE_PARAMS__;
  // no AuthGate so Playwright can load it. Not for humans.
  {
    path: "/bake-harness",
    element: <BakeHarnessPage />,
  },
  // /moodboard-smoke-test — DEV-ONLY A/B harness (Jun 15 evening).
  // Pick a moodboard + product + vision/render models + style-ref mode;
  // backend deconstructs the moodboard into a structured ad-construction
  // spec, renders the product against it. Used to figure out which
  // (vision, render, ref-mode) combo actually transfers aesthetic.
  {
    path: "/moodboard-smoke-test",
    element: <MoodboardSmokeTestPage />,
  },
  // /responsive-lab — THROWAWAY dev tool (mobile-optimized branch). Renders
  // the same app route in N iframes at different breakpoints and mirrors your
  // interactions across all panes, so you walk a flow once and every
  // breakpoint keeps up. Delete this route + the page file when done.
  {
    path: "/responsive-lab",
    element: <ResponsiveLabPage />,
  },
  {
    path: "/gallery",
    element: (
      <AuthGate>
        <GalleryPage />
      </AuthGate>
    ),
  },
  // /creatives/:id — full-page detail surface for one post. Click any
  // tile on /gallery to land here. Houses the unified publish (FB+IG
  // multi-select), reimagine-with-AI, schedule, download, and delete
  // affordances. Bookmarkable; cold-loads via GET /api/gallery/posts/:id.
  {
    path: "/creatives/:id",
    element: (
      <AuthGate>
        <CreativeDetailPage />
      </AuthGate>
    ),
  },
  // /editor-v4/:id — PROTOTYPE full-screen (Figma/Canva-style) editor. Isolated
  // from /creatives/:id (which stays the live editor). Own 100vh shell, no app
  // sidebar. Same Model-A overlays, so creatives round-trip between the two.
  {
    path: "/editor-v4/:id",
    element: (
      <AuthGate>
        <EditorV4Page />
      </AuthGate>
    ),
  },
  // /editor-v5/:id — PROTOTYPE editor, alt layout: left tools/actions, big
  // centered canvas, right 2-col (layer list | properties). v4 stays as-is.
  {
    path: "/editor-v5/:id",
    element: (
      <AuthGate>
        <EditorV5Page />
      </AuthGate>
    ),
  },
  // /connect-facebook — page picker shown after OAuth when the user
  // granted access to 2+ Facebook Pages. Single-page grants auto-
  // finalize and skip this screen entirely. See backend/scripts/
  // facebook_pending_connections.sql for the temp staging table.
  {
    path: "/connect-facebook",
    element: (
      <AuthGate>
        <ConnectFacebookPage />
      </AuthGate>
    ),
  },
  // /analytics/:channel — per-platform analytics surface. Linked from
  // the sidebar's Facebook / Instagram rows (replacing the disconnect-
  // modal-on-click pattern). channel ∈ {facebook, instagram}.
  {
    path: "/analytics/:channel",
    // V1 no-social: FB/IG analytics is off until the FB integration ships.
    // Redirect any direct URL to the gallery. Flip SOCIAL_PUBLISHING_ENABLED
    // to restore the page.
    element: (
      <SocialGate
        on={
          <AuthGate>
            <AnalyticsPage />
          </AuthGate>
        }
        off={<Navigate to="/gallery" replace />}
      />
    ),
  },
  // /schedule — content calendar. Phase 1: manual scheduling of approved
  // posts (no actual publishing yet — Phase 3 wires Meta posting once
  // OAuth is connected). See backend/scripts/scheduler.sql for the
  // scheduled_posts schema.
  {
    path: "/schedule",
    // V1 no-social: Ad Calendar is "coming soon" (nothing to publish without
    // social). Sidebar entry is disabled; redirect any direct URL too.
    element: (
      <SchedulingGate
        on={
          <AuthGate>
            <SchedulePage />
          </AuthGate>
        }
        off={<Navigate to="/gallery" replace />}
      />
    ),
  },
  // /schedule/auto — single-screen auto-schedule wizard. Pick a cadence,
  // choose source (AI ads or pre-approved posts), platforms, done. Backend
  // creates an auto_schedule_batches row + N placeholder scheduled_posts
  // tiles. The Slice B worker (not yet built) renders the AI ads
  // pre-week. See backend/scripts/scheduler.sql Phase 2 block.
  {
    path: "/schedule/auto",
    // V1 no-social: Ad Pilot is "coming soon". Sidebar entry disabled;
    // redirect direct URLs too. Flip SCHEDULING_ENABLED to restore.
    element: (
      <SchedulingGate
        off={<Navigate to="/gallery" replace />}
        on={
      <AuthGate>
        <PremiumRoute
          feature="Ad Pilot"
          icon="ri-flight-takeoff-line"
          tagline="Put your ad creation on autopilot — fresh, on-brand ads generated and scheduled for you every week."
          points={[
            { icon: 'ri-calendar-schedule-line', title: 'Set a cadence', body: 'Pick how often you want to post — we generate + schedule the ads to match.' },
            { icon: 'ri-sparkling-2-line', title: 'On-brand every time', body: 'Ads built from your brand kit + competitor intel, so they always fit.' },
            { icon: 'ri-loop-right-line', title: 'Set it and forget it', body: 'Stay consistently active without touching the generator each week.' },
            { icon: 'ri-calendar-check-line', title: 'Straight to your calendar', body: 'Every ad lands on your Ad Calendar, ready to review or publish.' },
          ]}
        >
          <AutoSchedulePage />
        </PremiumRoute>
      </AuthGate>
        }
      />
    ),
  },
  {
    path: "/demo",
    element: <DemoPage />,
  },
  {
    // Public "Try It" teaser (no AuthGate) — anonymous ad landing.
    path: "/try",
    element: <TryPage />,
  },
  {
    path: "/auth/sign-in",
    element: <SignInPage />,
  },
  {
    path: "/auth/sign-up",
    element: <SignUpPage />,
  },
  {
    path: "/auth/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/auth/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/auth/verify-email",
    element: <VerifyEmailPage />,
  },
  {
    path: "/auth/profile",
    element: (
      <AuthGate>
        <ProfilePage />
      </AuthGate>
    ),
  },
  {
    path: "/checkout",
    element: <CheckoutPage />,
  },
  {
    path: "/checkout/success",
    element: <CheckoutSuccessPage />,
  },
  {
    path: "/checkout/cancel",
    element: <CheckoutCancelPage />,
  },
  {
    path: "/privacy",
    element: <PrivacyPage />,
  },
  {
    path: "/privacy-app",
    element: <PrivacyShopifyPage />,
  },
  {
    path: "/terms",
    element: <TermsPage />,
  },
  {
    path: "/data-deletion",
    element: <DataDeletionPage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;